import { useCallback, useEffect, useRef } from 'react';
import { useDebounce } from './useDebounce';
import { devLog } from '@/utils/logger';
import { postsApi, type DraftAutosavePayload } from '@/services/posts';

/**
 * useServerDraftAutosave Hook
 *
 * Debounced (default 8s — deliberately longer than the 2s local
 * `useAutosave` debounce) mirror of a composer draft to the server-side
 * draft-autosave endpoint, for cross-device recovery.
 *
 * This is NOT the crash-safety layer. That's `useAutosave` (localStorage),
 * which is synchronous and always available even when the network/backend
 * is down. This hook is a best-effort mirror on top of it: it never blocks
 * typing, never blocks the local autosave, and swallows/logs its own
 * failures rather than surfacing them to the user.
 */

interface UseServerDraftAutosaveOptions<T> {
  /** Composer context this draft belongs to ('new' or an existing post id). */
  contextKey: string;
  /** Debounce delay in ms (default 8000). */
  delay?: number;
  /** Enable/disable the server mirror (e.g. only for new, non-edit posts). */
  enabled?: boolean;
  /**
   * Predicate used to decide whether `data` is "effectively empty" — mirrors
   * the local autosave's `isEmpty` guard so an empty draft can never
   * overwrite a real server-side draft (e.g. right after a form reset).
   */
  isEmpty?: (data: T) => boolean;
  /** Convert `data` into the JSON-safe wire payload sent to the server. */
  serialize: (data: T) => DraftAutosavePayload;
  /**
   * The composer's current create-intent key. Sent with EVERY 'new' autosave PUT once it exists (a key
   * appearing re-sends the unchanged draft right away). Not part of the payload, so it never defeats the
   * suppress latch. Null after the created post is adopted.
   */
  createIntentKey?: string | null;
  /**
   * Last-moment gate (read at write time): false while the composer is not a genuinely NEW, not-yet-saved
   * post (e.g. a created post was adopted). Nothing is mirrored then.
   */
  canWrite?: () => boolean;
}

export function useServerDraftAutosave<T>(data: T, options: UseServerDraftAutosaveOptions<T>): { suppressCurrentDraft: () => void } {
  const { contextKey, delay = 8000, enabled = true, isEmpty, serialize, createIntentKey: intentKey, canWrite } = options;
  const debouncedData = useDebounce(data, delay);
  const isFirstRender = useRef(true);
  // Last payload we successfully mirrored, so an unrelated re-render (or a
  // debounce tick with unchanged content) doesn't re-send the same draft.
  const lastSentSerialized = useRef<string | null>(null);
  // Live (non-debounced) data so suppressCurrentDraft can serialize synchronously.
  const currentDataRef = useRef<T>(data);
  currentDataRef.current = data;
  // Latch: serialized payload of the draft just cleared; skip re-mirroring it until content changes.
  const suppressedSerialized = useRef<string | null>(null);

  const prevDebouncedRef = useRef(debouncedData);
  const prevKeyRef = useRef(intentKey ?? null);

  useEffect(() => {
    // Triggers: the debounced draft moved, or the create-intent key APPEARED (null -> value). Never a key
    // being cleared, and never `enabled` / contextKey flipping: the debounced copy lags the live draft by up
    // to `delay`, so those flips (adoption, reset, leaving edit mode) would write STALE content.
    const debouncedChanged = prevDebouncedRef.current !== debouncedData;
    const keyAppeared = prevKeyRef.current === null && (intentKey ?? null) !== null;
    prevDebouncedRef.current = debouncedData;
    prevKeyRef.current = intentKey ?? null;

    // Skip first render — nothing has changed yet, and there's no need to
    // mirror whatever the composer happened to be seeded with on mount.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!debouncedChanged && !keyAppeared) return;
    if (!enabled) return;
    // Genuinely NEW, not-yet-saved post only (no adopted created post).
    if (canWrite && !canWrite()) return;

    // Always judge and write the LIVE draft, never the lagging debounced copy.
    const liveData = currentDataRef.current;

    // Never mirror an effectively-empty draft — it could silently overwrite
    // a genuine server-side draft (e.g. right after a form reset).
    if (isEmpty?.(liveData)) { suppressedSerialized.current = null; return; }

    const payload = serialize(liveData);
    const serializedForCompare = JSON.stringify(payload);

    if (suppressedSerialized.current !== null) {
      if (serializedForCompare === suppressedSerialized.current) return;
      suppressedSerialized.current = null;
    }

    const createIntentKey = contextKey === 'new' ? intentKey ?? undefined : undefined;
    const sentSignature = `${serializedForCompare}|${createIntentKey ?? ''}`;

    if (sentSignature === lastSentSerialized.current) return;

    const savedAt = Date.now();

    postsApi
      .putDraftAutosave({ contextKey, payload, savedAt, ...(createIntentKey ? { createIntentKey } : {}) })
      .then(() => {
        lastSentSerialized.current = sentSignature;
        devLog.log('[ServerDraftAutosave] Draft mirrored to server', { contextKey });
      })
      .catch((error: unknown) => {
        // Best-effort only: a failed server mirror must NEVER surface to the
        // user or interrupt the composer — the local autosave (useAutosave)
        // remains the source of truth for crash-safety regardless of
        // whether this succeeds.
        devLog.error('[ServerDraftAutosave] Failed to mirror draft to server', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedData, enabled, contextKey, intentKey]);

  const suppressCurrentDraft = useCallback((): void => {
    try {
      suppressedSerialized.current = JSON.stringify(serialize(currentDataRef.current));
    } catch (error) {
      devLog.error('[ServerDraftAutosave] Failed to latch cleared draft', error);
    }
  }, [serialize]);

  return { suppressCurrentDraft };
}
